import { useState, useMemo, useRef, ReactNode } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  Placement,
  useHover,
  arrow,
  size,
  safePolygon,
  limitShift,
  type UseFloatingOptions,
  type UseDismissProps,
} from "@floating-ui/react";
import type { ReferenceType } from "@floating-ui/react-dom"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { DisableCloseOnEscapeOrClickProps } from "../../types";

export type PopoverLegacyProps = DisableCloseOnEscapeOrClickProps & {
  children?: ReactNode;
  /**
   * Traps focus inside the floating element and outside content cannot be accessed
   */
  modal?: boolean;
  /**
   * Disables the shift of floating element
   * @internal
   */
  disableShift?: boolean;
  /**
   * Disables the flip of floating element
   * @internal
   */
  disableFlip?: boolean;
  /**
   * Disables the flip cross-axis fallback of floating element
   * @internal
   */
  disableFlipFallback?: boolean;
  /**
   * Changes interaction to use hover
   */
  openOnHover?: boolean;
  /**
   * Changes height and positioning for select/combobox
   */
  fillAvailableHeight?: boolean;
  maxHeight?: number | string;
  fitScreen?: boolean;
  minHeight?: number | string;
  matchReferenceWidth?: boolean;
  fixedWidth?: boolean;
  placement?: Placement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: UseFloatingOptions["onOpenChange"];
  onOutsidePress?: Exclude<UseDismissProps["outsidePress"], boolean>;
  /** This doesn't do anything for older browser */
  delay?: number;
  /**
   * Fires when the floating element is requests to be closed
   * Note: this does not fire when asTooltip is true and mouseleaves to close
   * eg. on clickoutside, [esc] to close, or by <Popover.Close>
   */
  onClose?: () => void;
  disableCaret?: boolean;
  noPadding?: boolean;
  disableAutoUpdate?: boolean;
  disableFocusLock?: boolean;
  disableTriggerFocus?: boolean;
  root?: HTMLElement | React.MutableRefObject<HTMLElement | null> | null;
};

export function usePopoverLegacy({
  defaultOpen,
  placement = "bottom",
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  onOutsidePress,
  onClose,
  disableCloseOnClickOutside,
  disableCloseOnEscape,
  openOnHover,
  fillAvailableHeight,
  fitScreen,
  maxHeight,
  minHeight = "2.875rem",
  matchReferenceWidth,
  fixedWidth,
  disableCaret,
  noPadding,
  disableShift,
  disableFlip,
  disableFlipFallback,
  disableAutoUpdate,
  disableFocusLock,
  disableTriggerFocus,
  root,
}: PopoverLegacyProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [labelId, setLabelId] = useState<string | undefined>();
  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = useRef(null);

  const processMinHeight =
    typeof minHeight === "string" ? minHeight : `${minHeight}px`;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: !disableAutoUpdate ? autoUpdate : undefined,
    middleware: [
      offset(8),
      size({
        padding: 5,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            ...(matchReferenceWidth
              ? { width: `${rects.reference.width}px` }
              : fixedWidth
                ? { width: "40dvw" }
                : {}),
            ...(fillAvailableHeight
              ? {
                  height: `${availableHeight - 20}px`,
                  minHeight: processMinHeight,
                }
              : {}),
            ...(maxHeight
              ? {
                  minHeight: processMinHeight,
                  height: fitScreen
                    ? typeof maxHeight === "string"
                      ? maxHeight
                      : `${maxHeight}px`
                    : undefined,
                  maxHeight: fitScreen
                    ? `${availableHeight - 20}px`
                    : typeof maxHeight === "string"
                      ? maxHeight
                      : `${maxHeight}px`,
                }
              : {}),
            ...(fitScreen && !maxHeight
              ? {
                  minHeight: processMinHeight,
                  maxHeight: `${availableHeight - 20}px`,
                }
              : {}),
            zIndex: 1,
          });
        },
      }),
      flip({
        mainAxis: !disableFlip,
        crossAxis: false,
        fallbackAxisSideDirection: disableFlipFallback ? "none" : "end",
        padding: 5,
      }),
      shift({
        padding: 5,
        mainAxis: !disableShift,
        crossAxis: false,
        limiter: limitShift({
          mainAxis: !disableShift,
        }),
      }),
      arrow({ element: !disableCaret ? arrowRef : null, padding: 5 }),
    ],
  });

  const context = data.context;

  const click = useClick(context, {
    enabled: controlledOpen == null && !openOnHover,
  });

  const hover = useHover(context, {
    enabled: controlledOpen == null && !!openOnHover,
    handleClose: safePolygon(),
  });

  const dismiss = useDismiss(context, {
    escapeKey: !disableCloseOnEscape,
    outsidePress: (event) => {
      if (disableCloseOnClickOutside) {
        return false;
      }

      // if onOutsidePress exists and calling it returns falsy, then skip
      // calling onClose and setOpen and return the falsy value to outsidePress
      const result =
        typeof onOutsidePress === "function" ? onOutsidePress?.(event) : true;

      if (!result) {
        return result;
      } else {
        onClose?.();
        setOpen(false);
        return result;
      }
    },
  });

  const role = useRole(context);

  const interactions = useInteractions([click, hover, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      onClose,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      arrowRef,
      disableCaret,
      noPadding,
      fillAvailableHeight,
      maxHeight,
      matchReferenceWidth,
      fixedWidth,
      disableFlipFallback,
      disableAutoUpdate,
      disableFocusLock,
      disableTriggerFocus,
      root,
    }),
    [
      open,
      setOpen,
      onClose,
      interactions,
      data,
      modal,
      labelId,
      descriptionId,
      disableCaret,
      noPadding,
      fillAvailableHeight,
      maxHeight,
      matchReferenceWidth,
      fixedWidth,
      disableFlipFallback,
      disableAutoUpdate,
      disableFocusLock,
      disableTriggerFocus,
      root,
    ],
  );
}
